import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Loader2 } from 'lucide-react';
import { SUPPORTED_LOCALES, type LocaleCode } from '../config/i18n';

const SWITCH_OVERLAY_MS = 280;

export default function LanguageSwitcher({
  className,
  variant = 'light',
}: {
  className?: string;
  variant?: 'light' | 'dark';
}) {
  const { i18n, t } = useTranslation();
  const [switching, setSwitching] = useState(false);
  const current = SUPPORTED_LOCALES.includes(i18n.language as LocaleCode) ? i18n.language : 'tr';
  const dark = variant === 'dark';

  const changeLanguage = async (locale: string) => {
    if (locale === current || switching) return;
    setSwitching(true);
    try {
      await i18n.changeLanguage(locale);
      await new Promise((r) => setTimeout(r, SWITCH_OVERLAY_MS));
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <div
        className={`inline-flex items-center overflow-hidden rounded-xl border ${
          dark
            ? 'border-lc-borderStrong'
            : 'border-gray-200 dark:border-lc-border p-0.5 gap-1 bg-white/80 dark:bg-lc-surface/80'
        } ${className ?? ''}`}
      >
        {!dark && (
          <Globe className="hidden sm:block h-3.5 w-3.5 text-gray-400 dark:text-lc-faint ml-1.5" aria-hidden />
        )}
        {SUPPORTED_LOCALES.map((locale) => {
          const active = current === locale;
          return (
            <button
              key={locale}
              type="button"
              onClick={() => void changeLanguage(locale)}
              disabled={switching}
              aria-pressed={active}
              className={
                dark
                  ? `px-2.5 py-1.5 text-xs font-semibold uppercase transition-colors disabled:opacity-60 ${
                      active ? 'bg-lc-pink text-lc-ink' : 'bg-lc-surface text-lc-muted hover:text-lc-text'
                    }`
                  : `px-2 py-1 rounded-lg text-xs font-semibold uppercase transition-colors disabled:opacity-60 ${
                      active
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 dark:text-lc-muted hover:bg-gray-50 dark:hover:bg-lc-surface'
                    }`
              }
            >
              {locale}
            </button>
          );
        })}
      </div>
      {switching &&
        createPortal(
          <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center ${
              dark ? 'bg-lc-bg/90' : 'bg-white/90 dark:bg-lc-bg/90'
            }`}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div
              className={`flex flex-col items-center gap-2 ${
                dark ? 'text-lc-muted' : 'text-gray-500 dark:text-lc-muted'
              }`}
            >
              <Loader2
                className={`h-7 w-7 animate-spin ${dark ? 'text-lc-pink' : 'text-primary-600'}`}
                aria-hidden
              />
              <span className="text-sm font-medium">{t('common.loading')}</span>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
